import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {VideoPlayerComponent} from '../video-player/video-player.component';
import {SlideNavigationComponent} from '../slide-navigation/slide-navigation.component';
import {KenBurnsService} from '../../../../../services/slide-show/ken-burns.service';
import {KenBurnsEffect} from '../../models/ken-burns-effect';

export interface MediaItem {
  url: string;
  type: 'image' | 'video';
  duration?: number;
}

// Using the KenBurnsEffect interface from models/ken-burns-effect.ts

@Component({
  selector: 'app-slide-display',
  standalone: true,
  imports: [CommonModule, VideoPlayerComponent, SlideNavigationComponent],
  templateUrl: './slide-display.component.html',
  styleUrl: './slide-display.component.scss'
})
export class SlideDisplayComponent implements OnChanges {
  // Inputs
  @Input() currentMedia!: MediaItem; // Current media item (image or video)
  @Input() controlsVisible: boolean = true; // Show video controls
  @Input() mobileControlsVisible: boolean = false; // Mobile control visibility
  @Input() kenBurnsEffect?: KenBurnsEffect; // Ken Burns effect for images
  @Input() slideIndex: number = 0; // Current slide index
  @Input() canGoNext: boolean = true; // Can navigate to next slide
  @Input() canGoPrevious: boolean = true; // Can navigate to previous slide

  // Outputs
  @Output() videoEnded = new EventEmitter<void>(); // Video playback ended
  @Output() videoTimeUpdate = new EventEmitter<void>(); // Video time updated
  @Output() videoPlay = new EventEmitter<void>(); // Video started playing
  @Output() videoPause = new EventEmitter<void>(); // Video paused
  @Output() videoLoadedMetadata = new EventEmitter<void>(); // Video metadata loaded
  @Output() next = new EventEmitter<void>(); // Request next slide
  @Output() previous = new EventEmitter<void>(); // Request previous slide
  @Output() mouseEnter = new EventEmitter<void>(); // Mouse entered slide content
  @Output() mouseLeave = new EventEmitter<void>(); // Mouse left slide content
  @Output() videoTouchStart = new EventEmitter<TouchEvent>(); // Video touch start
  @Output() videoTouchEnd = new EventEmitter<TouchEvent>(); // Video touch end
  @Output() videoControlsTouch = new EventEmitter<void>(); // Video controls touched

  // Component state
  mediaOrientation = signal<'landscape' | 'portrait'>('landscape');
  currentKenBurnsEffect?: KenBurnsEffect;
  // References
  @ViewChild('imageElement') imageElement?: ElementRef<HTMLImageElement>;
  @ViewChild('videoPlayer') videoPlayer?: VideoPlayerComponent;
  // Services
  private kenBurnsService = inject(KenBurnsService);

  ngOnChanges(changes: SimpleChanges): void {
    // Reset orientation to landscape default when media changes
    if (changes['currentMedia']) {
      this.mediaOrientation.set('landscape');
    }

    // If kenBurnsEffect changes, apply the new effect
    if (changes['kenBurnsEffect'] && this.currentMedia?.type === 'image') {
      this.currentKenBurnsEffect = this.kenBurnsEffect;

      // Reset and apply the effect using the service if we have an image element
      setTimeout(() => {
        if (this.imageElement && this.currentKenBurnsEffect) {
          this.resetKenBurnsEffect();
          // Small delay to ensure reset takes effect before reapplying
          setTimeout(() => {
            if (this.currentKenBurnsEffect) {
              this.kenBurnsService.applyEffect(this.imageElement!.nativeElement, this.currentKenBurnsEffect);
            }
          }, 50);
        }
      }, 0);
    } else if (!this.kenBurnsEffect && this.currentMedia?.type === 'image') {
      // Default Ken Burns effect if none provided
      this.currentKenBurnsEffect = {
        type: 'zoom-in',
        duration: 5000,
        startScale: 1,
        endScale: 1.2,
        easing: 'ease-in-out'
      };
    }

    // Reset Ken Burns effect when slide index changes (jumping between slides)
    if (changes['slideIndex'] && this.currentMedia?.type === 'image') {
      setTimeout(() => {
        this.resetKenBurnsEffect();
        // Small delay to ensure reset takes effect before reapplying
        setTimeout(() => {
          if (this.imageElement && this.currentKenBurnsEffect) {
            this.kenBurnsService.applyEffect(this.imageElement.nativeElement, this.currentKenBurnsEffect);
          }
        }, 50);
      }, 0);
    }
  }

  // Orientation detection
  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.naturalWidth && img.naturalHeight) {
      this.mediaOrientation.set(
        img.naturalWidth >= img.naturalHeight ? 'landscape' : 'portrait'
      );
    }
  }

  // Media event handlers
  onVideoTimeUpdate(): void {
    this.videoTimeUpdate.emit();
  }

  onVideoEnded(): void {
    this.videoEnded.emit();
  }

  onVideoPlay(): void {
    this.videoPlay.emit();
  }

  onVideoPause(): void {
    this.videoPause.emit();
  }

  onVideoLoadedMetadata(dimensions: {width: number, height: number}): void {
    this.videoLoadedMetadata.emit();
    if (dimensions.width && dimensions.height) {
      this.mediaOrientation.set(
        dimensions.width >= dimensions.height ? 'landscape' : 'portrait'
      );
    }
  }

  // Navigation handlers
  onNextSlide(): void {
    this.next.emit();
  }

  onPreviousSlide(): void {
    this.previous.emit();
  }

  // Mouse event handlers
  onMouseEnter(): void {
    this.mouseEnter.emit();
  }

  onMouseLeave(): void {
    this.mouseLeave.emit();
  }

  // Touch event handlers
  onVideoTouchStart(event: TouchEvent): void {
    this.videoTouchStart.emit(event);
  }

  onVideoTouchEnd(event: TouchEvent): void {
    this.videoTouchEnd.emit(event);
  }

  onVideoControlsTouch(): void {
    this.videoControlsTouch.emit();
  }

  // Container touch handlers as fallback
  onContainerTouchStart(event: TouchEvent): void {
    // Only handle if it's a video and not touching controls
    if (this.currentMedia.type === 'video') {
      const target = event.target as HTMLElement;
      const isControlElement = target.closest('app-video-controls') ||
        target.closest('.video-controls-overlay') ||
        target.classList.contains('video-control-btn') ||
        target.classList.contains('video-progress-container');

      if (!isControlElement) {
        this.onVideoTouchStart(event);
      }
    }
  }

  onContainerTouchEnd(event: TouchEvent): void {
    // Only handle if it's a video and not touching controls
    if (this.currentMedia.type === 'video') {
      const target = event.target as HTMLElement;
      const isControlElement = target.closest('app-video-controls') ||
        target.closest('.video-controls-overlay') ||
        target.classList.contains('video-control-btn') ||
        target.classList.contains('video-progress-container');

      if (!isControlElement) {
        this.onVideoTouchEnd(event);
      }
    }
  }

  /**
   * Restart the video from the beginning (for looping functionality)
   */
  restartVideo(): void {
    if (this.videoPlayer && this.currentMedia.type === 'video') {
      this.videoPlayer.restartVideo();
    }
  }

  /**
   * Get the video element (for external control if needed)
   */
  getVideoElement(): HTMLVideoElement | null {
    if (this.videoPlayer && this.currentMedia.type === 'video') {
      return this.videoPlayer.getVideoElement();
    }
    return null;
  }

  /**
   * Force sync video state (for fixing display issues)
   */
  forceSyncVideoState(): void {
    if (this.videoPlayer && this.currentMedia.type === 'video') {
      this.videoPlayer.forceSyncState();
    }
  }

  // Reset Ken Burns effect by removing animation classes and resetting styles
  private resetKenBurnsEffect(): void {
    if (this.imageElement) {
      const imageContainer = this.imageElement.nativeElement.parentElement;

      if (imageContainer) {
        // Remove all Ken Burns effect classes from the container
        imageContainer.classList.remove(
          'zoom-in',
          'zoom-out',
          'pan-left',
          'pan-right',
          'pan-up',
          'pan-down'
        );

        // Reset transform and animation styles on the container
        imageContainer.style.transform = '';
        imageContainer.style.animation = '';
        imageContainer.style.animationName = '';
        imageContainer.style.animationDuration = '';

        // Force reflow to ensure styles are applied
        imageContainer.offsetHeight;
      }
    }
  }

  // Ken Burns effect generator
  private generateKenBurnsEffect(): KenBurnsEffect {
    const effectTypes: Array<KenBurnsEffect['type']> = ['zoom-in', 'zoom-out', 'pan-left', 'pan-right'];
    const randomType = effectTypes[Math.floor(Math.random() * effectTypes.length)];
    const duration = 8000 + Math.random() * 4000; // 8-12 seconds

    return {type: randomType, duration};
  }
}
